import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi, productsApi, walletApi, ordersApi, paymentsApi, settingsApi } from '../api';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('cs_token'));
  const [authLoading, setAuthLoading] = useState(true);

  const [products, setProducts] = useState([]);
  const [wallet, setWallet] = useState({ usdt: 0, inr: 0 });
  const [txns, setTxns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [storeSettings, setStoreSettings] = useState({ store_name: 'PREMIUM VAULT', notice: '', telegram_bot_username: '', support_telegram: '' });

  const [notifications, setNotifications] = useState([]);

  // Bootstrap: try to fetch current user with token
  useEffect(() => {
    (async () => {
      if (!token) { setAuthLoading(false); return; }
      try {
        const u = await authApi.me();
        setUser(u);
      } catch (e) {
        localStorage.removeItem('cs_token');
        setToken(null);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [token]);

  // Fetch data when user logs in
  const refreshProducts = useCallback(async () => {
    try { setProducts(await productsApi.list()); } catch (e) { /* noop */ }
  }, []);
  const refreshWallet = useCallback(async () => {
    try {
      const w = await walletApi.get();
      setWallet({ usdt: w.usdt || 0, inr: w.inr || 0 });
      setTxns(w.txns || []);
    } catch (e) { /* noop */ }
  }, []);
  const refreshOrders = useCallback(async () => {
    try { setOrders(await ordersApi.list()); } catch (e) { /* noop */ }
  }, []);
  const refreshPayments = useCallback(async () => {
    try { setPaymentMethods(await paymentsApi.list()); } catch (e) { /* noop */ }
  }, []);
  const refreshSettings = useCallback(async () => {
    try { setStoreSettings(await settingsApi.get()); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { refreshSettings(); }, [refreshSettings]);

  useEffect(() => {
    if (!user) return;
    refreshProducts(); refreshWallet(); refreshOrders(); refreshPayments();
    const iv = setInterval(() => {
      refreshProducts(); refreshWallet(); refreshOrders();
    }, 20000);
    return () => clearInterval(iv);
  }, [user, refreshProducts, refreshWallet, refreshOrders, refreshPayments]);

  // Derive notifications from orders + txns (latest events)
  useEffect(() => {
    const items = [];
    orders.slice(0, 5).forEach((o) => {
      items.push({
        id: 'o-' + o.id,
        title: o.status === 'DELIVERED' ? 'Order Delivered' : o.status === 'PENDING' ? 'Order Pending' : 'Order Update',
        body: `${o.product_name} — ${o.status}`,
        time: o.created_at,
        unread: o.status === 'PENDING',
      });
    });
    setNotifications(items);
  }, [orders]);

  const login = async ({ username, password }) => {
    const res = await authApi.login({ username, password });
    localStorage.setItem('cs_token', res.token);
    setToken(res.token);
    setUser(res.user);
    return res;
  };

  const signup = async ({ display_name, username, password }) => {
    const res = await authApi.register({ display_name, username, password });
    localStorage.setItem('cs_token', res.token);
    setToken(res.token);
    setUser(res.user);
    return res;
  };

  const logout = () => {
    localStorage.removeItem('cs_token');
    setToken(null);
    setUser(null);
    setProducts([]); setOrders([]); setTxns([]); setWallet({ usdt: 0, inr: 0 });
  };

  const submitDeposit = async ({ currency, amount, txn_ref, payment_method_id }) => {
    const res = await walletApi.deposit({ currency, amount: Number(amount), txn_ref, payment_method_id });
    await refreshWallet();
    return res;
  };

  const buy = async (product, currency) => {
    try {
      const order = await ordersApi.create({ product_id: product.id, currency });
      await Promise.all([refreshWallet(), refreshOrders(), refreshProducts()]);
      return { ok: true, order };
    } catch (e) {
      return { ok: false, msg: e?.response?.data?.detail || 'Purchase failed' };
    }
  };

  const markAllRead = () => setNotifications((ns) => ns.map((n) => ({ ...n, unread: false })));

  return (
    <AppContext.Provider value={{
      user, token, authLoading,
      login, signup, logout,
      products, refreshProducts,
      wallet, txns, refreshWallet,
      orders, refreshOrders,
      paymentMethods, refreshPayments,
      storeSettings, refreshSettings,
      submitDeposit, buy,
      notifications, markAllRead,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
